import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { FaCalendarAlt, FaUsers, FaEye, FaClock } from 'react-icons/fa';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('30');

  // User growth data (12 months)
  const userGrowthData = [
    { month: 'Jan', users: 1200 },
    { month: 'Feb', users: 1350 },
    { month: 'Mar', users: 1180 },
    { month: 'Apr', users: 1420 },
    { month: 'May', users: 1580 },
    { month: 'Jun', users: 1720 },
    { month: 'Jul', users: 1650 },
    { month: 'Aug', users: 1890 },
    { month: 'Sep', users: 2100 },
    { month: 'Oct', users: 1950 },
    { month: 'Nov', users: 2250 },
    { month: 'Dec', users: 2400 }
  ];

  // Post engagement data
  const postEngagementData = [
    { day: 'Mon', posts: 45, engagement: 120 },
    { day: 'Tue', posts: 52, engagement: 150 },
    { day: 'Wed', posts: 38, engagement: 95 },
    { day: 'Thu', posts: 61, engagement: 180 },
    { day: 'Fri', posts: 49, engagement: 140 },
    { day: 'Sat', posts: 33, engagement: 85 },
    { day: 'Sun', posts: 28, engagement: 70 }
  ];

  // User role distribution
  const roleData = [
    { name: 'Admin', value: 5, color: '#3B82F6' },
    { name: 'Editor', value: 12, color: '#10B981' },
    { name: 'Viewer', value: 123, color: '#F59E0B' }
  ];

  // Key metrics
  const metrics = [
    {
      title: 'Total Visits',
      value: '45,678',
      change: '+12.5%',
      changeType: 'positive',
      icon: FaEye,
      color: 'blue'
    },
    {
      title: 'Bounce Rate',
      value: '24.3%',
      change: '-2.1%',
      changeType: 'positive',
      icon: FaUsers,
      color: 'green'
    },
    {
      title: 'Avg Session Duration',
      value: '4m 32s',
      change: '+8.7%',
      changeType: 'positive',
      icon: FaClock,
      color: 'purple'
    },
    {
      title: 'Page Views',
      value: '123,456',
      change: '+15.2%',
      changeType: 'positive',
      icon: FaCalendarAlt,
      color: 'orange'
    }
  ];

  const MetricCard = ({ metric }) => {
    const Icon = metric.icon;
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {metric.title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {metric.value}
            </p>
            <div className={`flex items-center mt-2 ${
              metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
            }`}>
              <span className="text-sm font-medium">{metric.change}</span>
            </div>
          </div>
          <div className={`p-3 rounded-full ${colorClasses[metric.color]} bg-opacity-10`}>
            <Icon className={`text-2xl ${colorClasses[metric.color].replace('bg-', 'text-')}`} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track your community's performance and engagement
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <MetricCard key={index} metric={metric} />
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            User Growth (12 Months)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="month"
                stroke="#6B7280"
                fontSize={12}
              />
              <YAxis
                stroke="#6B7280"
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Post Engagement Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Post Engagement by Day
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={postEngagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="day"
                stroke="#6B7280"
                fontSize={12}
              />
              <YAxis
                stroke="#6B7280"
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar
                dataKey="engagement"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* User Role Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          User Role Distribution
        </h3>
        <div className="flex flex-col lg:flex-row items-center">
          <div className="w-full lg:w-1/2">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={roleData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {roleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full lg:w-1/2 lg:pl-6">
            <div className="space-y-4">
              {roleData.map((role, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: role.color }}
                    ></div>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {role.name}
                    </span>
                  </div>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">
                    {role.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;